import type { Requirement } from "./requirements.ts";

/**
 * The carrier directory (docs/carrier-runs.md#carrier-directory): where each carrier's portal is,
 * so `start_run` can open a run's browser there without the producer giving the URL. A URL passed
 * to `start_run` always wins, since new carriers come all the time.
 */

/**
 * One carrier: its name, the other names producers use for it, and its portal's start page; for a
 * carrier we quote through a carrier skill, also the lines it quotes and what it needs before its
 * portal is opened (requirements.ts).
 */
export type Carrier = {
  readonly name: string;
  readonly aliases: readonly string[];
  readonly portalUrl: string;
  readonly lines?: string;
  readonly requirements?: readonly Requirement[];
};

// Where a client's mortgage loan holds a value (the crm-pull skill), read before it is asked.
const BORROWER_INFO = "the loan's Borrower Info";
const CURRENT_POLICY =
  "the current policy in the loan's documents (declarations page, evidence of insurance or invoice)";
const DRIVER_LICENCE = "the borrower's driver's licence in the loan's documents";

// Requirements shared by the carriers below, in the order the producer is asked.

const firstName: Requirement = {
  key: "firstName",
  label: "First name",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's first name",
};
const lastName: Requirement = {
  key: "lastName",
  label: "Last name",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's last name",
};
const businessName: Requirement = {
  key: "businessName",
  label: "Business name",
  from: "client",
  need: "provide",
  ask: "The business's legal name",
};
const dateOfBirth: Requirement = {
  key: "dateOfBirth",
  label: "Date of birth",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's date of birth",
  lookIn: `${BORROWER_INFO}, or ${DRIVER_LICENCE}`,
};
const phones: Requirement = {
  key: "phones",
  label: "Phone",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's phone number",
  lookIn: BORROWER_INFO,
};
const propertyAddress: Requirement = {
  key: "address",
  label: "Property address",
  from: "property",
  need: "provide",
  verifyWith: "producer",
  ask: "The property's street, city, state and ZIP",
  lookIn: "the loan's subject property",
};
const effectiveDate: Requirement = {
  key: "effectiveDate",
  label: "Effective date",
  from: "quote",
  need: "provide",
  future: true,
  verifyWith: "choice",
  ask: "The date the policy should start",
};
/**
 * The current policy's premium. No carrier asks it: the producer compares ours with it, so it is
 * kept, with its source, like any value the quote is priced on.
 */
const priorPremium: Requirement = {
  key: "priorPremium",
  label: "Current premium",
  from: "property",
  need: "confirm",
  verifyWith: "producer",
  ask: "The current policy's annual premium, to compare with ours",
  lookIn: CURRENT_POLICY,
};
const premiumPayer: Requirement = {
  key: "premiumPayer",
  label: "Who pays the premium",
  from: "quote",
  need: "provide",
  verifyWith: "producer",
  ask: "Who pays the premium: the insured, or the mortgagee (escrow)?",
  lookIn: "the Closing Disclosure in the loan's documents: whether insurance is escrowed",
};
const mortgagee: Requirement = {
  key: "mortgagee",
  label: "Mortgagee",
  from: "property",
  need: "provide",
  when: { key: "premiumPayer", equals: "mortgagee" },
  verifyWith: "producer",
  ask: "The mortgage lender's name and the loan number",
  lookIn: `${CURRENT_POLICY}, or the payoff demand`,
};
/** Facts the carriers fill in from public records; the producer checks them. */
const prefilled = (
  ...keys: ("yearBuilt" | "construction" | "roofType" | "squareFeet" | "stories" | "units")[]
): Requirement[] =>
  keys.map((key) => ({
    key,
    label: PREFILLED_LABELS[key],
    from: "property",
    need: "confirm",
    ask: `The ${PREFILLED_LABELS[key].toLowerCase()}, if the carrier's value looks wrong`,
  }));
const PREFILLED_LABELS = {
  yearBuilt: "Year built",
  construction: "Construction",
  roofType: "Roof type",
  squareFeet: "Living area (sq ft)",
  stories: "Stories",
  units: "Units",
} as const;

/** National General's Custom360 Home (homeowners), as the recorded quote of 2026-10-05 asked it. */
const NATIONAL_GENERAL: Requirement[] = [
  firstName,
  lastName,
  dateOfBirth,
  {
    key: "gender",
    label: "Gender",
    from: "client",
    need: "provide",
    ask: "The insured's gender (female or male)",
    lookIn:
      "the borrower's driver's licence or state ID in the loan's documents; never the 1003's demographics",
  },
  {
    key: "maritalStatus",
    label: "Marital status",
    from: "client",
    need: "provide",
    assume: { value: "single", basis: "assumed; the loan shows a spouse when there is one" },
    ask: "The insured's marital status (single, married, divorced, separated, widowed)",
    lookIn: BORROWER_INFO,
  },
  {
    key: "occupation",
    label: "Occupation",
    from: "answer",
    need: "provide",
    assume: { value: "none of these", basis: "assumed; few clients are in these" },
    ask: "Is the insured an athlete or entertainer, in a high-profile profession, a journalist, in the military, a politician, a student, or none of these?",
  },
  phones,
  {
    key: "serviceConsent",
    label: "Consent to service calls and texts",
    from: "answer",
    need: "provide",
    assume: { value: "no", basis: "the client has not been asked" },
    ask: "Does the insured consent to policy and driver service calls and texts (yes or no)?",
  },
  propertyAddress,
  {
    key: "occupancy",
    label: "Occupancy",
    from: "property",
    need: "provide",
    assume: { value: "primary, owner-occupied", basis: "assumed for a homeowners quote" },
    verifyWith: "producer",
    ask: "How the home is used: primary, secondary (seasonal or not) or vacant, and owner- or tenant-occupied",
    lookIn: `${BORROWER_INFO}: the 1003's occupancy`,
  },
  {
    key: "namedInsuredType",
    label: "Named insured is",
    from: "answer",
    need: "provide",
    assume: { value: "owner", basis: "the client is the borrower on the loan" },
    ask: "Is the insured the owner, a tenant or a life tenant of the home?",
  },
  {
    key: "datePurchased",
    label: "Date purchased",
    from: "property",
    need: "provide",
    verifyWith: "producer",
    ask: "When the insured bought the home",
    lookIn:
      "the loan's documents: the title commitment's vesting deed (read all its pages), a purchase loan's Closing Disclosure (its closing date), or the appraisal's prior sale; failing those, the time at the current address (owned) in Borrower Info, as an approximate date",
  },
  {
    key: "hailResistantRoof",
    label: "Hail-resistant roof",
    from: "answer",
    need: "provide",
    assume: { value: "no", basis: "assumed; the cautious answer, which may price higher" },
    ask: "Is the roof hail (impact) resistant (yes or no)?",
  },
  {
    key: "roofYear",
    label: "Roof replaced",
    from: "property",
    need: "confirm",
    ask: "The year the roof was last fully replaced, if known",
    lookIn: "the appraisal or the seller's disclosure in the loan's documents",
  },
  ...prefilled("yearBuilt", "construction", "roofType", "squareFeet", "stories"),
  {
    key: "vehiclesGaraged",
    label: "Vehicles garaged at the home",
    from: "answer",
    need: "provide",
    assume: { value: 0, basis: "assumed" },
    ask: "How many vehicles are garaged at the home?",
  },
  {
    key: "priorCarrier",
    label: "Prior carrier",
    from: "property",
    need: "provide",
    assume: { value: "Other insurance companies", basis: "the loan shows no current policy" },
    verifyWith: "producer",
    ask: "Who insures the home now, or 'none' if it has no insurance",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "priorPolicyExpiration",
    label: "Prior policy expires",
    from: "property",
    need: "provide",
    when: { key: "priorCarrier", notEquals: "none" },
    future: true,
    assume: {
      derive: "sameAs:effectiveDate",
      basis: "the loan shows no current policy; the new one starts when it ends",
    },
    verifyWith: "producer",
    ask: "When the current policy expires",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "yearsInsured",
    label: "Years continuously insured",
    from: "property",
    need: "provide",
    when: { key: "priorCarrier", notEquals: "none" },
    assume: {
      derive: "yearsSince:datePurchased",
      basis: "insured without a gap since the purchase",
    },
    ask: "How many years the home has been insured without a gap",
    lookIn: `${CURRENT_POLICY}; otherwise it follows from the purchase date`,
  },
  priorPremium,
  {
    ...effectiveDate,
    assume: { derive: "tomorrow", basis: "the earliest start; the loan shows no current policy" },
  },
  {
    ...premiumPayer,
    assume: { value: "insured", basis: "assumed; the loan's escrow decides when it shows one" },
  },
  mortgagee,
  {
    key: "deductible",
    label: "All perils deductible",
    from: "quote",
    need: "provide",
    assume: {
      value: "$1,000",
      basis: "the loan shows no current policy; a common choice, the agency may set its own",
    },
    verifyWith: "choice",
    ask: "The all perils deductible: $500, $1,000, $1,500, $2,000, $2,500, $5,000, $7,500, $10,000, or 0.5%, 1%, 2%, 3% or 5%",
  },
  {
    key: "dwellingCoverage",
    label: "Dwelling (Coverage A)",
    from: "quote",
    need: "confirm",
    verifyWith: "choice",
    ask: "A dwelling limit, if not the carrier's replacement cost",
  },
  {
    key: "liabilityLimit",
    label: "Liability",
    from: "quote",
    need: "confirm",
    verifyWith: "choice",
    ask: "A liability limit, if not the carrier's $100,000",
  },
  {
    key: "bundle",
    label: "Bundle with other products",
    from: "answer",
    need: "provide",
    assume: { value: "no", basis: "home only, so the portal asks nothing about auto" },
    verifyWith: "choice",
    ask: "Should this be bundled with another National General product, such as auto (yes or no)?",
  },
  {
    key: "eligibility",
    label: "Eligibility questions",
    from: "answer",
    need: "provide",
    assume: { value: "no to all", basis: "assumed; the client has not been asked" },
    ask: "Yes or no for each: in the past 5 years a foreclosure, bankruptcy, judgment or lien; exotic or vicious animals; home on pilings, piers or an open foundation; knob-and-tube or aluminum wiring or existing damage; home for sale; on a historic register; short-term rentals (Airbnb, Vrbo); a skateboard or bicycle ramp or a treehouse over 12 feet; commercial or farming exposure; accidents or claims in the past 30 days",
  },
  {
    key: "driverLicence",
    label: "Driver's licence",
    from: "answer",
    need: "in-portal",
    ask: "The insured's driver's licence status, state and number, which National General asks for at its last step",
    lookIn: `${DRIVER_LICENCE}; typed into the portal, never saved`,
  },
];

/** Steadily's landlord policy (DP3), as the recorded new quote of 2026-10-05 asked it. */
const STEADILY: Requirement[] = [
  firstName,
  lastName,
  {
    key: "ownerType",
    label: "Owned by",
    from: "answer",
    need: "provide",
    ask: "Is the property owned by a person or a company (such as an LLC)?",
  },
  {
    key: "businessName",
    label: "Company",
    from: "client",
    need: "provide",
    when: { key: "ownerType", equals: "company" },
    ask: "The name of the company that owns the property",
  },
  dateOfBirth,
  {
    key: "mailingAddress",
    label: "Owner's mailing address",
    from: "client",
    need: "provide",
    ask: "The owner's mailing address, if not the property",
  },
  {
    key: "emails",
    label: "Email",
    from: "client",
    need: "provide",
    ask: "The owner's email address",
    lookIn: BORROWER_INFO,
  },
  phones,
  propertyAddress,
  {
    key: "occupancy",
    label: "Occupancy",
    from: "property",
    need: "provide",
    ask: "How the property is used: rented out, seasonal, or owner-occupied with a tenant",
  },
  ...prefilled("units", "yearBuilt", "construction", "roofType", "squareFeet", "stories"),
  {
    key: "daysUninsured",
    label: "Days uninsured",
    from: "property",
    need: "provide",
    ask: "How many days the property has been without insurance (0 if none)",
  },
  priorPremium,
  {
    key: "shortTermRental",
    label: "Short-term rental",
    from: "answer",
    need: "provide",
    ask: "Is it rented for less than 3 months at a time, such as a vacation rental (yes or no)?",
  },
  {
    key: "propertyManager",
    label: "Third-party property manager",
    from: "answer",
    need: "provide",
    ask: "Does a third-party property manager look after it (yes or no)?",
  },
  {
    key: "centralAlarm",
    label: "Central station alarm",
    from: "answer",
    need: "provide",
    ask: "Does it have a central station fire or theft alarm (yes or no)?",
  },
  {
    key: "multiLocation",
    label: "Multi-location",
    from: "answer",
    need: "provide",
    ask: "Does the owner insure other properties with Steadily, or want several on this policy (yes or no)?",
  },
  effectiveDate,
  premiumPayer,
  mortgagee,
  {
    key: "deductible",
    label: "All other perils deductible",
    from: "quote",
    need: "confirm",
    ask: "A deductible, if not Steadily's $2,500: $5,000, $10,000, or 1%, 2%, 3%, 5% or 10%",
  },
  {
    key: "dwellingCoverage",
    label: "Dwelling",
    from: "quote",
    need: "confirm",
    ask: "A dwelling limit, if not Steadily's estimate (it allows up to about 20% more)",
  },
  {
    key: "liabilityLimit",
    label: "Premises liability",
    from: "quote",
    need: "confirm",
    ask: "A liability limit, if not Steadily's $300,000",
  },
];

/**
 * The agency's carriers. Add each real carrier here: the name producers say, its aliases (short
 * names, former names), and the page its agent portal starts at, usually the sign-in page.
 */
export const AGENCY_CARRIERS: readonly Carrier[] = [
  {
    name: "National General",
    aliases: ["NatGen", "NGIC", "National General Insurance"],
    portalUrl: "https://natgenagency.com/Login.aspx?Menu=Login",
    lines: "homeowners (Custom360 Home)",
    requirements: NATIONAL_GENERAL,
  },
  {
    name: "Steadily",
    aliases: ["Steadily Insurance"],
    portalUrl: "https://app.steadily.com/login/",
    lines: "landlord, rental dwelling (DP3)",
    requirements: STEADILY,
  },
];

/**
 * The capture lab's mock carriers, which the local end-to-end run uses. They listen on consecutive
 * ports from the first one (Acme Mutual's), so their URLs come from configuration.
 */
export const MOCK_CARRIERS = [
  {
    name: "Acme Mutual",
    aliases: ["Acme"],
    portOffset: 0,
    lines: "small business: general liability and business property (mock)",
    requirements: [
      businessName,
      propertyAddress,
      {
        key: "yearBuilt",
        label: "Year built",
        from: "property",
        need: "provide",
        ask: "The year the building was built",
      },
      effectiveDate,
    ],
  },
  {
    name: "Bison Insurance",
    aliases: ["Bison"],
    portOffset: 1,
    lines: "small business: general liability and business property (mock)",
    requirements: [businessName, propertyAddress, ...prefilled("roofType")],
  },
] as const satisfies readonly (Omit<Carrier, "portalUrl"> & { portOffset: number })[];

/** The mock carriers on `localhost`, from Acme Mutual's port. */
export function mockCarriers(firstPort: number): Carrier[] {
  return MOCK_CARRIERS.map((c) => ({
    name: c.name,
    aliases: c.aliases,
    portalUrl: `http://localhost:${firstPort + c.portOffset}/`,
    lines: c.lines,
    requirements: c.requirements,
  }));
}

export type CarrierDirectoryOptions = {
  /** Acme Mutual's port when the mock carriers run on this machine; without it they are left out. */
  mockCarriersPort?: number;
};

/** The directory a daemon uses: the agency's carriers, and the mock carriers when configured. */
export function carrierDirectory(options: CarrierDirectoryOptions = {}): Carrier[] {
  const port = options.mockCarriersPort;
  return [...AGENCY_CARRIERS, ...(port !== undefined ? mockCarriers(port) : [])];
}

/** How carriers are told apart: the name trimmed, spaces collapsed, case ignored. */
export function carrierKey(carrier: string): string {
  return carrier.trim().replace(/\s+/g, " ").toLowerCase();
}

/** The directory's carrier with this name or alias, by carrier key; the first one if several. */
export function findCarrier(directory: readonly Carrier[], name: string): Carrier | undefined {
  const key = carrierKey(name);
  return directory.find((c) => [c.name, ...c.aliases].some((n) => carrierKey(n) === key));
}

/**
 * The CRMs a producer pulls a client's loan from (the crm-pull skill), which a run names as its
 * carrier. A run at one reads the loan and prices nothing; each Arive company has its own host, so
 * their addresses are the producers' own (docs/decisions.md, 2026-10-23).
 */
export const CRMS: readonly Pick<Carrier, "name" | "aliases">[] = [
  { name: "Arive", aliases: ["myarive"] },
  { name: "LendingPad", aliases: ["Lending Pad"] },
];

/** Whether a run's carrier is a CRM rather than an insurance carrier. */
export function isCrm(name: string): boolean {
  const key = carrierKey(name);
  return CRMS.some((crm) => [crm.name, ...crm.aliases].some((alias) => carrierKey(alias) === key));
}
